import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import AuthModal from './AuthModal';
import { BootDots } from './ui/BootDots';
import IOSInstallGuide, { useIOSInstallGuide } from './IOSInstallGuide';

import { DemoModal } from './landing/DemoModal';

const SEO_ROUTES: Record<string, { h1: string; h2: string; features: string[] }> = {
  '/nutrition': {
    h1: 'AI Nutrition Tracker & Calorie Counter',
    h2: 'Track calories, protein, carbs, fat and fiber with AI photo scanning',
    features: ['Scan food with your camera for instant nutrition data', 'Search 500,000+ foods in our database', 'AI menu scanner reads restaurant menus', 'Track macros with personalized goals based on your body', 'Fiber tracking across all features'],
  },
  '/fridge': {
    h1: 'AI Fridge Scanner - Get Recipes from Your Fridge',
    h2: 'Take a photo of your fridge and get instant recipe suggestions',
    features: ['AI analyzes what is in your fridge from a photo', 'Get recipe suggestions with full nutrition breakdown', 'Calorie and macro data for every recipe', 'Works with any fridge or pantry photo'],
  },
  '/workouts': {
    h1: 'AI Workout Plan Generator & Gym Tracker with APRE 10 Autoregulation',
    h2: 'Personalized gym programs with evidence-based APRE 10 (Mann protocol) progressive overload',
    features: ['AI generates workout plans for your goals', 'Track sets, reps, weight for every exercise', 'APRE 10 autoregulation — auto-adjusts weights based on your last set reps (Mann protocol)', 'Automatic deload weeks (bodyweight: reps drop 25%, loaded: weight drops 25%)', 'Training history and personal records', 'Works offline in the gym'],
  },
  '/walking': {
    h1: 'Walking Tracker & Step Counter',
    h2: 'Track steps, distance, and calories burned on your walks',
    features: ['Background step counting and GPS tracking', 'Automatic calorie burn calculation', 'Walking history with daily and weekly stats', 'Syncs with your nutrition goals automatically'],
  },
  '/statistics': {
    h1: 'Fitness Statistics & AI Body Fat Analysis',
    h2: 'Track your progress with body measurements and AI photo analysis',
    features: ['Body weight trend charts over time', 'Body measurement tracking (chest, arms, waist, etc.)', 'AI body fat percentage estimation from photos', 'Personal records for all exercises', 'Progress photo before/after comparisons'],
  },
  '/photos': {
    h1: 'Progress Photos with AI Body Fat Analysis',
    h2: 'Upload physique photos and get instant AI body fat estimation',
    features: ['Automatic body fat percentage analysis using AI', 'Goal timeline: estimated time to reach 10%, 12.5%, 15% body fat', 'Before/after photo comparisons', 'Body fat trend chart over time in Statistics'],
  },
};

function SeoRouteContent() {
  const route = window.location.pathname;
  const seo = SEO_ROUTES[route];
  if (!seo) return null;
  return (
    <section className="max-w-4xl mx-auto px-6 py-12 text-center">
      <h1 className="text-3xl sm:text-4xl font-bold font-['Space_Grotesk'] text-[#f1f3fc] mb-3">{seo.h1}</h1>
      <h2 className="text-base sm:text-lg text-[#a8abb3] mb-8">{seo.h2}</h2>
      <ul className="text-left max-w-xl mx-auto space-y-3">
        {seo.features.map((f, i) => (
          <li key={i} className="flex items-start gap-3 text-[#c8cad0] text-sm">
            <span className="text-[#cafd00] mt-0.5 flex-shrink-0">&#10003;</span>
            {f}
          </li>
        ))}
      </ul>
    </section>
  );
}
import { NavBar } from './landing/NavBar';
import { HeroSection } from './landing/HeroSection';
import { StaticBodyBackdrops } from './landing/StaticBodyBackdrops';
import { FeaturesSection } from './landing/FeaturesSection';
import { HowItWorksSection } from './landing/HowItWorksSection';
import { AiCoachSection } from './landing/AiCoachSection';
import { ScreenshotsGallery } from './landing/ScreenshotsGallery';
import { PricingSection } from './landing/PricingSection';
import { FaqSection } from './landing/FaqSection';
import { LatestFromBlogSection } from './landing/LatestFromBlogSection';
import { CtaSection } from './landing/CtaSection';
import { LandingFooter } from './landing/LandingFooter';

export default function LandingPage({ onSelectApp, onOpenCalculators, onOpenFatAI }) {
  const { user, isAuthenticated, loading } = useAuth();
  const [authMode, setAuthMode] = useState<'login' | 'register'>('login');
  const [showDemo, setShowDemo] = useState(false);
  

  const observeEl = useCallback((el: HTMLElement | null) => {
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('visible');
        obs.unobserve(entry.target);
      }
    }, { threshold: 0.15 });
    obs.observe(el);
  }, []);
  
  const { showGuide, openGuide, closeGuide, canShow: canShowIOSInstall } = useIOSInstallGuide();
  
  const [resetToken] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('reset_token') || null;
  });

  const [showAuthModal, setShowAuthModal] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return !!urlParams.get('reset_token');
  });

  useEffect(() => {
    if (isAuthenticated && !loading) {
      onSelectApp('notebook');
    }
  }, [isAuthenticated, loading, onSelectApp]);

  const handleOpenAuth = useCallback((mode: 'login' | 'register') => {
    setAuthMode(mode);
    setShowAuthModal(true);
  }, []);

  if (loading) {
    return <BootDots testId="landing-loading" />;
  }

  if (isAuthenticated) {
    return <BootDots testId="landing-authed-loading" />;
  }

  return (
    <div className="min-h-screen bg-[#0a0e14] text-[#f1f3fc] overflow-x-hidden">
      <AuthModal 
        open={showAuthModal} 
        onOpenChange={setShowAuthModal} 
        defaultTab={authMode}
        resetToken={resetToken}
      />
      <IOSInstallGuide forceShow={showGuide} onClose={closeGuide} />
      <DemoModal showDemo={showDemo} onClose={() => setShowDemo(false)} />

      {/* Fixed bodybuilder backdrops (visible at every scroll position).
          Sits at z-0 / fixed; content below is wrapped in z-10 so it scrolls over the imagery. */}
      <StaticBodyBackdrops />

      <NavBar
        onOpenCalculators={onOpenCalculators}
        onOpenFatAI={onOpenFatAI}
        onOpenAuth={handleOpenAuth}
        canShowIOSInstall={canShowIOSInstall}
        openGuide={openGuide}
      />
      <div className="relative z-10">
        {/* SEO content for route-specific pages — visible to Googlebot */}
        <SeoRouteContent />
        <HeroSection onOpenAuth={handleOpenAuth} />
        <FeaturesSection observeEl={observeEl} />
        <ScreenshotsGallery />
        <PricingSection onOpenAuth={handleOpenAuth} />
        <LatestFromBlogSection />
        <FaqSection />
        <LandingFooter />
      </div>
    </div>
  );
}
